import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const RazorpayPayment = ({ 
  service, 
  user, 
  location, 
  country, 
  fullName, 
  address, 
  exchangeRate, 
  gstin, 
  selectedFruit, 
  companyType, 
  selectedState, 
  hasSSN, 
  registeredAgent, 
  additionalPrice, 
  hours, 
  promoCodeDetails,
  renderButton 
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const loadScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadScript();
  }, []);

  // Promo discount in dollars
  const getPromoDiscountDollars = (originalPrice) => {
    if (!promoCodeDetails) return 0;
    
    let discountableAmount = originalPrice;
    
    // special logic for service_1
    if (service._id === 'service_1') {
        if (promoCodeDetails.applicableFee === 'Our Fee') discountableAmount = 300;
        else if (promoCodeDetails.applicableFee === 'Total') {
            // originalPrice already represents the total before discount for service_1
            discountableAmount = originalPrice;
        }
    } else { // For other services, 'Total' is the only applicableFee
        discountableAmount = originalPrice;
    }

    if (promoCodeDetails.discountType === 'fixed') {
      return Math.min(promoCodeDetails.discountValue, discountableAmount);
    }
    return discountableAmount * (promoCodeDetails.discountValue || 0) / 100;
  };

  const calculateAmount = () => {
    if (!service) return 0;

    // For service_1 (USA Company Formation)
    if (service._id === 'service_1') {
      let price = 300; // Base formation fee
      
      // Add state fee if available
      if (selectedState) {
        const stateFees = {
          LLC: {
            Wyoming: 104,
            Delaware: 110,
            'New Jersey': 130,
            Texas: 310
          },
          'C-corporation': {
            Wyoming: 104,
            Delaware: 109,
            'New Jersey': 130,
            Texas: 310
          }
        };
        
        if (companyType && selectedState) {
          price += stateFees[companyType][selectedState];
        }
      }
      
      // Add EIN fee based on SSN
      if (hasSSN === true) {
        price += 55;
      } else if (hasSSN === false) {
        price += 210;
      }
      
      // Add registered agent fee if not included
      if (registeredAgent === false) {
        price += 125;
      }

      // Apply promo code discount
      if (promoCodeDetails) {
        price = price - getPromoDiscountDollars(price);
      }
      
      if (location === 'india') {
        const baseAmount = price * exchangeRate;
        const gstAmount = baseAmount * 0.18;
        return Math.round((baseAmount + gstAmount) * 100); // Convert to paise
      }
      
      return Math.round(price * 100); // Convert to cents
    }
    
    // Default case for other services (service_2, service_3, service_8, and general default)
    let price = service.price;
    if (service._id === 'service_2' && typeof additionalPrice !== 'undefined') price += additionalPrice;
    if (service._id === 'service_3' && hours) price *= hours;
    // service_8 already has its price set by service.price

    if (promoCodeDetails) {
        price -= getPromoDiscountDollars(price);
    }

    if (location === 'india') {
      const baseAmount = price * exchangeRate;
      const gstAmount = baseAmount * 0.18;
      return Math.round((baseAmount + gstAmount) * 100); // Convert to paise
    }

    return Math.round(price * 100); // Convert to cents
  };

  const getDisplayAmount = () => {
    if (!service) return '';

    // For service_1 (USA Company Formation)
    if (service._id === 'service_1') {
      let price = 300;
      if (selectedState) {
          const stateFees = { LLC: { Wyoming: 104, Delaware: 110, 'New Jersey': 130, Texas: 310 }, 'C-corporation': { Wyoming: 104, Delaware: 109, 'New Jersey': 130, Texas: 310 } };
          if (companyType && selectedState) {
            price += stateFees[companyType][selectedState];
          }
      }
      if (hasSSN === true) price += 55; else if (hasSSN === false) price += 210;
      if (registeredAgent === false) price += 125;

      if (promoCodeDetails) price -= getPromoDiscountDollars(price);
      
      if (location === 'india') {
        const baseAmount = price * exchangeRate;
        const gstAmount = baseAmount * 0.18;
        return `₹${(baseAmount + gstAmount).toFixed(2)}`;
      }
      return `$${price.toFixed(2)}`;
    }
    
    // Default case for other services (service_2, service_3, service_8, and general default)
    let price = service.price;
    if (service._id === 'service_2' && typeof additionalPrice !== 'undefined') price += additionalPrice;
    if (service._id === 'service_3' && hours) price *= hours;
    // service_8 already has its price set by service.price

    if (promoCodeDetails) price -= getPromoDiscountDollars(price);

    if (location === 'india') {
        const baseAmount = price * exchangeRate;
        const gstAmount = baseAmount * 0.18;
        return `₹${(baseAmount + gstAmount).toFixed(2)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  const handlePayment = async () => {
    try {
      // Validate all required fields
      if (!service || !user?._id || !location || !fullName || !address) {
        throw new Error('Missing required fields');
      }
      if (location === 'international' && !country) {
        throw new Error('Please select your country');
      }
      if (!window.Razorpay) throw new Error('Payment gateway is loading');

      const amount = calculateAmount();
      const currency = location === 'india' ? 'INR' : 'USD';
      const receipt = `rcpt_${user._id.slice(-6)}_${Date.now().toString().slice(-4)}`;

      // Calculate the price before discount to pass to getPromoDiscountDollars for accurate discount amount
      let priceBeforeDiscount = service.price;
      if (service._id === 'service_1') {
        priceBeforeDiscount = 300; // Base formation fee
        if (selectedState) {
          const stateFees = {
            LLC: { Wyoming: 104, Delaware: 110, 'New Jersey': 130, Texas: 310 },
            'C-corporation': { Wyoming: 104, Delaware: 109, 'New Jersey': 130, Texas: 310 }
          };
          if (companyType && selectedState) {
            priceBeforeDiscount += stateFees[companyType][selectedState];
          }
        }
        if (hasSSN === true) priceBeforeDiscount += 55; else if (hasSSN === false) priceBeforeDiscount += 210;
        if (registeredAgent === false) priceBeforeDiscount += 125;
      } else if (service._id === 'service_2' && typeof additionalPrice !== 'undefined') {
        priceBeforeDiscount += additionalPrice;
      } else if (service._id === 'service_3' && hours) {
        priceBeforeDiscount *= hours;
      }
      const promoDiscountAmount = promoCodeDetails ? getPromoDiscountDollars(priceBeforeDiscount) : 0;

      const orderResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount,
          currency,
          receipt,
          notes: {
            price: service.price,
            fullName,
            address,
            location,
            country: location === 'international' ? country : 'India',
            service: service.name,
            userId: user._id,
            serviceId: service._id,
            exchangeRate: location === 'india' ? exchangeRate : undefined,
            gstApplied: location === 'india',
            selectedFruit: location === 'india' ? selectedFruit : undefined,
            gstin: location === 'india' ? gstin : undefined,
            email: user.email,
            phone: user.phone,
            promoCodeId: promoCodeDetails ? promoCodeDetails._id : undefined,
            promoDiscount: promoDiscountAmount
          }
        })
      });

      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      const order = await orderResponse.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "BooksComply",
        description: `Payment for ${service.name}`,
        image: "/favicon.png",
        order_id: order.id,
        handler: async function (response) {
          try {
            const verifyResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/payment/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                orderId: order.id
              })
            });

            const verification = await verifyResponse.json();

            if (!verifyResponse.ok || !verification.success) {
              throw new Error(verification.message || 'Payment verification failed');
            }

            toast.success('Payment Successful!');
            navigate(`/payment-success?payment_id=${response.razorpay_payment_id}&order_id=${response.razorpay_order_id}`);
          } catch (error) {
            toast.error(`Payment verification failed: ${error.message}`);
            navigate('/pricing');
          }
        },
        prefill: {
          name: user.name || fullName,
          email: user.email || '',
          contact: user.phone || ''
        },
        theme: {
          color: '#4a6baf',
          hide_topbar: false
        },
        modal: {
          ondismiss: () => {
            toast.info('Payment window closed');
          },
          escape: true,
          backdropclose: false
        },
        notes: {
          service: service.name,
          location,
          country: location === 'international' ? country : 'India'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      toast.error(`Payment error: ${error.message}`);
      console.error('Payment error:', error);
    }
  };

  // If renderButton prop is provided, use it to render the button
  if (renderButton) {
    return renderButton(handlePayment);
  }

  // Default button rendering if no renderButton prop is provided
  return (
    <button
      className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold hover:shadow-lg hover:shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      onClick={handlePayment}
      disabled={!service || !user || (location === 'international' && !country)}
    >
      Proceed to Pay {getDisplayAmount()}
    </button>
  );
};

export default RazorpayPayment;
